import { useState, useEffect } from 'react';
import { useLocation, NavLink } from 'react-router-dom';
import axios from 'axios';
import MenuDataFallback from '../../json/menu_admin_new.json';
import { FaSignOutAlt } from 'react-icons/fa';
import Menus from './Menus';
import CollapsedMenu from './CollapsedMenu';
import { canAccess } from '../../helper/permissions';
import WebsiteLogo from '../WebsiteLogo';
import { useWebsiteSettings } from '../../hooks/useWebsiteSettings';
import { UNAUTH_ADMIN_REDIRECT } from '../../helper/authPaths';

const WEBSITE_SETTINGS_MENU = {
    name: 'Setting Website',
    path: '/admin/website-settings',
    icon: 'FaCog',
    menu_key: 'payment',
    sub: [],
};

function withWebsiteSettingsMenu(items, permissions) {
    if (!Array.isArray(items) || !canAccess(permissions, 'payment', 'edit')) {
        return items;
    }

    const exists = items.some(
        (item) =>
            item.path === '/admin/website-settings' ||
            item.sub?.some((sub) => sub.path === '/admin/website-settings'),
    );

    if (exists) return items;
    return [...items, WEBSITE_SETTINGS_MENU];
}

const SidebarAdmin = ({ data, isCollapsed, showSidebar, setShowSidebar }) => {
    useLocation();
    const { settings } = useWebsiteSettings(data?.token);
    const [menuData, setMenuData] = useState(MenuDataFallback);
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < 768 : false,
    );

    useEffect(() => {
        const role = data?.user?.role;
        if (!role) return;
        axios
            .get(`api/menu-access/sidebar/${role}`, {
                headers: { Authorization: `Bearer ${data.token}` },
                withCredentials: true,
            })
            .then((res) => {
                const items =
                    Array.isArray(res.data) && res.data.length
                        ? res.data
                        : MenuDataFallback;
                setMenuData(withWebsiteSettingsMenu(items, data?.user?.permissions));
            })
            .catch(() => {
                setMenuData(
                    withWebsiteSettingsMenu(MenuDataFallback, data?.user?.permissions),
                );
            });
    }, [data?.user?.role, data?.token, data?.user?.permissions]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 768;
            setIsMobile(mobile);
            if (!mobile) {
                setShowSidebar('left-0');
            } else {
                setShowSidebar('-left-72');
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [setShowSidebar]);

    const logout = async () => {
        try {
            await axios.post('api/auth/logout', {}, { withCredentials: true });
        } catch (error) {
            console.error('Logout error:', error);
        } finally {
            window.location.href = UNAUTH_ADMIN_REDIRECT;
        }
    };

    const closeOnMobile = (value) => {
        setShowSidebar(value);
        if (window.innerWidth < 768) {
            setTimeout(() => setShowSidebar('-left-72'), 100);
        }
    };

    const showExpanded = isMobile || !isCollapsed;
    const sidebarWidth = isMobile ? 'w-[min(280px,88vw)]' : isCollapsed ? 'w-[72px]' : 'w-[260px]';

    return (
        <>
            {showSidebar === 'left-0' && isMobile && (
                <div
                    className="fixed inset-0 z-20 bg-carbon/40 md:hidden"
                    onClick={() => setShowSidebar('-left-72')}
                />
            )}
            <div className="z-30">
                <div
                    className={`fixed top-0 z-30 h-screen md:left-0 ${showSidebar} ${sidebarWidth} transition-all duration-tesla`}
                >
                    <aside className="sidebar-shell relative flex h-full flex-col border-r border-hairline dark:border-white/10">
                        <button
                            onClick={() => setShowSidebar('-left-72')}
                            className="absolute right-2 top-2 z-40 rounded-xs p-2 text-muted hover:bg-surface-soft md:hidden"
                            aria-label="Close sidebar"
                            type="button"
                        >
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className={`sidebar-divider border-b ${showExpanded ? 'px-5 py-5' : 'px-2 py-4'}`}>
                            <NavLink to="/admin" className="block" onClick={() => isMobile && setShowSidebar('-left-72')}>
                                {showExpanded ? (
                                    <>
                                        <WebsiteLogo
                                            token={data?.token}
                                            alt="Logo"
                                            className="h-12 w-full object-contain object-left md:px-2"
                                        />
                                        <p className="mt-2 text-[10px] font-medium uppercase tracking-widest text-muted">
                                            {settings.systemTitle}
                                        </p>
                                        <div className="mt-3 h-0.5 w-10 bg-accent-brand" aria-hidden />
                                    </>
                                ) : (
                                    <div className="mx-auto h-9 w-9 overflow-hidden">
                                        <WebsiteLogo
                                            token={data?.token}
                                            alt="Logo"
                                            className="h-9 w-full object-contain object-left"
                                        />
                                    </div>
                                )}
                            </NavLink>
                        </div>

                        {showExpanded ? (
                            <>
                                <div className="flex-1 overflow-y-auto px-3 py-4">
                                    <Menus menuData={menuData} setShowSidebar={closeOnMobile} />
                                </div>
                                <div className="sidebar-divider border-t p-3">
                                    <button
                                        type="button"
                                        onClick={logout}
                                        className="sidebar-logout flex w-full items-center gap-3 rounded-xs px-3 py-2.5 text-sm transition-colors"
                                    >
                                        <FaSignOutAlt className="text-xs" />
                                        <span>Log Out</span>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex-1 px-1.5 py-3">
                                <CollapsedMenu menuData={menuData} setShowSidebar={closeOnMobile} />
                            </div>
                        )}
                    </aside>
                </div>
            </div>
        </>
    );
};

export default SidebarAdmin;
